import confetti from 'canvas-confetti';
import { useRef, useMemo, useState, useEffect } from 'react';

import { Box, Stack, Dialog, Button, useTheme, Skeleton, Typography } from '@mui/material';

import { useUserStore } from 'src/stores/user/user.store';
import { useAnalyticsContext } from 'src/providers/analytics-provider';

import Image from '../image';

export interface ConfettiDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function ConfettiDialog({ open, onClose }: ConfettiDialogProps) {
  const theme = useTheme();
  const [userPointsNotifications, isLoadingUserPointsNotifications] = useUserStore((state) => [
    state.userPointsNotifications,
    state.isLoadingUserPointsNotifications,
  ]);

  const { setOpenSendNotes } = useUserStore();
  const { logAction } = useAnalyticsContext();

  const [showDialog, setShowDialog] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [hasStartedAnimation, setHasStartedAnimation] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const confettiRef = useRef<any>(null);
  const timersRef = useRef<number[]>([]);
  const intervalsRef = useRef<number[]>([]);

  const colors = useMemo(
    () => [
      theme.palette.redBrand?.[300] ?? '#FF4D4F',
      theme.palette.blueBrand?.[400] ?? '#1677FF',
      theme.palette.yellowBrand[300] ?? '#FADB14',
      theme.palette.greenBrand[400] ?? '#52C41A',
      '#ffffff',
    ],
    [theme]
  );

  const clearTimers = () => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  };
  const clearIntervals = () => {
    intervalsRef.current.forEach((id) => clearInterval(id));
    intervalsRef.current = [];
  };
  const cleanupCanvas = () => {
    clearTimers();
    clearIntervals();
    if (canvasRef.current && canvasRef.current.parentElement) {
      canvasRef.current.parentElement.removeChild(canvasRef.current);
    }
    canvasRef.current = null;
    confettiRef.current = null;
  };

  const mountCanvas = () => {
    const c = document.createElement('canvas');
    c.setAttribute('aria-hidden', 'true');
    Object.assign(c.style, {
      position: 'fixed',
      inset: '0',
      width: '100vw',
      height: '100vh',
      pointerEvents: 'none',
      zIndex: '9999',
    });
    document.body.appendChild(c);
    canvasRef.current = c;
    confettiRef.current = confetti.create(c, {
      resize: true,
      useWorker: true,
    });
  };

  const fire = () => {
    if (!confettiRef.current) return;

    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const baseDuration = prefersReduced ? 1200 : 2000;

    const fountainsMs = prefersReduced ? 300 : 600;
    const fountainsInterval = window.setInterval(() => {
      confettiRef.current!({
        particleCount: prefersReduced ? 20 : 40,
        startVelocity: prefersReduced ? 25 : 45,
        spread: 55,
        angle: 60,
        origin: { x: 0.1, y: 0.9 },
        ticks: 200,
        gravity: 1.0,
        scalar: 0.9,
        colors,
        shapes: ['circle', 'square'],
        drift: -0.6,
        decay: 0.92,
      });
      confettiRef.current!({
        particleCount: prefersReduced ? 20 : 40,
        startVelocity: prefersReduced ? 25 : 45,
        spread: 55,
        angle: 120,
        origin: { x: 0.9, y: 0.9 },
        ticks: 200,
        gravity: 1.0,
        scalar: 0.9,
        colors,
        shapes: ['circle', 'square'],
        drift: 0.6,
        decay: 0.92,
      });
    }, 160);
    intervalsRef.current.push(fountainsInterval);

    timersRef.current.push(
      window.setTimeout(() => {
        clearInterval(fountainsInterval);
      }, fountainsMs)
    );

    const rainStart = fountainsMs + 80;
    timersRef.current.push(
      window.setTimeout(() => {
        const end = Date.now() + (prefersReduced ? 400 : 900);
        const rainInterval = window.setInterval(() => {
          const timeLeft = end - Date.now();
          if (timeLeft <= 0) {
            clearInterval(rainInterval);
            return;
          }
          const progress = timeLeft / (prefersReduced ? 400 : 900);
          confettiRef.current!({
            particleCount: Math.max(10, Math.floor(40 * progress)),
            spread: 75,
            startVelocity: 15,
            origin: { x: Math.random() * 0.8 + 0.1, y: -0.1 },
            ticks: 300,
            gravity: 0.9,
            scalar: 0.8,
            colors,
            shapes: ['circle', 'square'],
            drift: (Math.random() - 0.5) * 1.2,
            decay: 0.91,
          });
        }, 120);
        intervalsRef.current.push(rainInterval);
      }, rainStart)
    );

    const finaleAt = Math.max(rainStart + (prefersReduced ? 350 : 800), 600);
    timersRef.current.push(
      window.setTimeout(() => {
        confettiRef.current!({
          particleCount: prefersReduced ? 60 : 120,
          spread: 70,
          startVelocity: prefersReduced ? 25 : 55,
          origin: { x: 0.5, y: 0.4 },
          ticks: 250,
          gravity: 0.95,
          scalar: 1.0,
          colors,
          shapes: ['square', 'circle'],
          decay: 0.9,
        });
        confettiRef.current!({
          particleCount: prefersReduced ? 10 : 20,
          spread: 60,
          startVelocity: 45,
          origin: { x: 0.5, y: 0.4 },
          ticks: 260,
          gravity: 1.0,
          scalar: 1.4,
          colors,
          shapes: ['square'],
          decay: 0.92,
        });
      }, finaleAt)
    );

    timersRef.current.push(
      window.setTimeout(() => {
        setShowDialog(true);
      }, baseDuration)
    );

    timersRef.current.push(
      window.setTimeout(() => {
        setShowContent(true);
      }, baseDuration + 300)
    );
  };

  useEffect(() => {
    if (open && !isLoadingUserPointsNotifications && !hasStartedAnimation) {
      setHasStartedAnimation(true);
      setShowDialog(false);
      setShowContent(false);
      mountCanvas();
      fire();
    }
  }, [open, isLoadingUserPointsNotifications, hasStartedAnimation]);

  useEffect(() => {
    if (!open) {
      setShowDialog(false);
      setShowContent(false);
      setHasStartedAnimation(false);
      cleanupCanvas();
    }
  }, [open]);

  useEffect(() => () => cleanupCanvas(), []);

  const translateTier = (tier?: string): string => {
    const translations: Record<string, string> = {
      silver: 'Prata',
      gold: 'Ouro',
      diamond: 'Diamante',
    };

    return translations[tier?.toLowerCase() ?? ''] ?? tier ?? '';
  };

  if (!open) return null;

  return (
    <Dialog
      open={showDialog}
      onClose={() => {
        cleanupCanvas();
        onClose();
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          position: 'relative',
          overflow: 'visible',
          transition: 'all 0.5s ease-in-out',
        },
      }}
    >
      <Stack
        p={12}
        bgcolor={theme.palette.common.white}
        borderRadius={4}
        alignItems="center"
        spacing={2}
        sx={{
          transition: 'all 0.5s ease-in-out',
        }}
      >
        {isLoadingUserPointsNotifications ? (
          <Stack spacing={8} width="100%" alignItems="center">
            <Skeleton variant="circular" width={120} height={120} />
            <Stack spacing={2} width="100%">
              <Skeleton variant="text" width="80%" height={32} sx={{ mx: 'auto' }} />
              <Skeleton variant="text" width="90%" height={32} sx={{ mx: 'auto' }} />
            </Stack>
            <Skeleton variant="text" width="100%" height={24} />
            <Skeleton variant="text" width="85%" height={20} />
          </Stack>
        ) : (
          <>
            <Box
              sx={{
                transform: showContent ? 'rotateY(540deg)' : 'rotateY(0deg)',
                transition: 'transform 1.2s ease-in-out',
                maxWidth: 180
              }}
            >
              <Image src="/assets/star-hand.png" />
            </Box>

            <Stack spacing={8}>
              <Stack>
                <Typography variant="h5" color={theme.palette.redBrand[300]} textAlign="center">
                  <b>Falta pouco!</b>
                </Typography>
                <Typography variant="h5" color={theme.palette.redBrand[300]} textAlign="center">
                  <b>
                    Você ganhou {userPointsNotifications?.notificationData?.validatedPoints} pontos.
                  </b>
                </Typography>
              </Stack>
              <Typography variant="paragraphM700" color={theme.palette.grey[600]}>
                Mais {userPointsNotifications?.notificationData?.remainingPoints} pontos e você
                desbloqueia as vantagens do nível{' '}
                {translateTier(userPointsNotifications?.notificationData?.nextTier)}
              </Typography>
              <Typography variant="paragraphP500" color={theme.palette.common.neutral[600]}>
                Continue escaneando suas notas fiscais.
              </Typography>

              <Box
                sx={{
                  maxHeight: showContent ? '500px' : '0px',
                  opacity: showContent ? 1 : 0,
                  overflow: 'hidden',
                  transition: 'all 0.8s ease-in-out',
                }}
              >
                <Stack spacing={2} pt={8}>
                  <Button
                    sx={{
                      bgcolor: theme.palette.blueBrand[400],
                      color: theme.palette.common.white,
                      '&:hover': {
                        bgcolor: theme.palette.blueBrand[400],
                      },
                      '&:active': {
                        bgcolor: theme.palette.blueBrand[400],
                      },
                      '&:focus': {
                        bgcolor: theme.palette.blueBrand[400],
                      },
                    }}
                    onClick={() => setOpenSendNotes(true, logAction)}
                  >
                    Escanear mais notas
                  </Button>
                  <Button
                    sx={{
                      bgcolor: theme.palette.common.white,
                      color: theme.palette.blueBrand[400],
                    }}
                    variant="text"
                    style={{ textDecoration: 'underline' }}
                    onClick={onClose}
                  >
                    Fechar
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </>
        )}
      </Stack>
    </Dialog>
  );
}
